import type { ComponentType, ReactNode, SVGProps } from "react";
import {
  AlignLeftIcon,
  BanknoteIcon,
  Building2Icon,
  CalendarClockIcon,
  CircleCheckIcon,
  Columns3Icon,
  EyeIcon,
  FileTextIcon,
  HashIcon,
  LandmarkIcon,
  ListChecksIcon,
  MailIcon,
  MapPinIcon,
  Settings2Icon,
  ShieldCheckIcon,
  TagsIcon,
  UserRoundIcon,
  UsersIcon,
  WalletCardsIcon,
} from "lucide-react";

import { cn } from "../../../lib/utils";

export type DataTableColumnHeaderIcon = ComponentType<SVGProps<SVGSVGElement>>;

export type DataTableColumnHeaderProps = {
  icon: DataTableColumnHeaderIcon;
  label: ReactNode;
  align?: "left" | "right";
  className?: string;
};

const HEADER_ICON_RULES: Array<{
  pattern: RegExp;
  icon: DataTableColumnHeaderIcon;
}> = [
  // `(?<!trans)action` — NOT a bare /action/. Ids are matched as substrings, and "trans-action-Reference"
  // contains "action", so a bare pattern handed the Reference column the settings icon (and right-aligned its
  // header). A word-start anchor is not enough either: "historyActions" is a real id whose "action" is
  // mid-word. Excluding the "trans" prefix is what actually distinguishes an ACTION column from a TRANSACTION
  // one. Same class of trap as the account/count collision noted below.
  { pattern: /(?<!trans)(action|menu|option|setting)/, icon: Settings2Icon },
  // Must precede the Hash rule: "financialAccount" normalises to "financialaccount", whose
  // "ac-count" substring otherwise matches /count/ and hands every account column a `#` icon.
  { pattern: /account|payment/, icon: WalletCardsIcon },
  { pattern: /reference|code|number|count|quantity|occurrence/, icon: HashIcon },
  { pattern: /amount|balance|total|currency|debit|credit|price|cost|target|remaining|realized/, icon: BanknoteIcon },
  { pattern: /date|time|createdat|updatedat|submitted|registered|planned|baptized|expires|viewed|period|schedule|calendar/, icon: CalendarClockIcon },
  { pattern: /status|state|lifecycle/, icon: CircleCheckIcon },
  { pattern: /category|type|kind|module/, icon: TagsIcon },
  { pattern: /fund/, icon: LandmarkIcon },
  { pattern: /branch|workspace|department|parish/, icon: Building2Icon },
  { pattern: /member|members|users|team|group|ministers|candidates|responsibles/, icon: UsersIcon },
  { pattern: /owner|holder|leader|actor|submitter|approver|createdby|recordedby|customer|vendor|contributor/, icon: UserRoundIcon },
  { pattern: /email/, icon: MailIcon },
  { pattern: /role|permission|access/, icon: ShieldCheckIcon },
  { pattern: /scope|visibility/, icon: EyeIcon },
  { pattern: /location|address/, icon: MapPinIcon },
  { pattern: /description|notes|purpose/, icon: AlignLeftIcon },
  { pattern: /document|file|report/, icon: FileTextIcon },
  { pattern: /program|task|queue/, icon: ListChecksIcon },
]

export function resolveDataTableColumnHeaderIcon(columnId: string): DataTableColumnHeaderIcon {
  const normalizedColumnId = columnId.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()
  return HEADER_ICON_RULES.find((rule) => rule.pattern.test(normalizedColumnId))?.icon ?? Columns3Icon
}

/**
 * Column ids whose cells are numeric/right-aligned, so their header must follow.
 *
 * `(?<!trans)action`: a bare /action/ also matches "transactionReference", which right-aligned the Reference
 * header while its cells stayed left — header and data disagreed. See the icon rules above for the same trap.
 */
const RIGHT_ALIGNED_COLUMN_PATTERN =
  /(?<!trans)(action|menu|option|setting)|amount|balance|total|debit|credit|price|cost/i;

/**
 * The column's alignment, header and cells alike.
 *
 * Exported so the BODY can resolve alignment the same way the header does. They used to disagree: the header
 * right-aligned itself from the id heuristic while cells right-aligned only from an explicit `meta.align`, so
 * any money column that did not set the meta — Funds' Balance, for one — rendered a right-aligned header over
 * left-aligned figures. Two rules for one column is the bug; this is the one rule.
 */
export function resolveDataTableColumnAlign(
  columnId: string,
  explicitAlign?: "left" | "right",
): "left" | "right" {
  return explicitAlign ?? (RIGHT_ALIGNED_COLUMN_PATTERN.test(columnId) ? "right" : "left");
}

export function DataTableAutomaticColumnHeader({
  columnId,
  label,
  align: alignOverride,
}: {
  columnId: string;
  label: ReactNode;
  /** Explicit alignment (from a column's `meta.align`) — overrides the numeric-id heuristic below. */
  align?: "left" | "right";
}) {
  const align = resolveDataTableColumnAlign(columnId, alignOverride);
  return (
    <DataTableColumnHeader
      icon={resolveDataTableColumnHeaderIcon(columnId)}
      label={label}
      align={align}
    />
  );
}

export function DataTableColumnHeader({
  icon: Icon,
  label,
  align = "left",
  className,
}: DataTableColumnHeaderProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap",
        align === "right" && "w-full justify-end",
        className,
      )}
    >
      <Icon aria-hidden="true" className="size-3.5 shrink-0 text-foreground" />
      <span className="text-foreground">{label}</span>
    </span>
  );
}
